import type {Duration, ConferenceIntegrations} from 'src/types/scheduler';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';


type TFunction = (key: string, value: string) => string;

import format from 'date-fns/format';
import isToday from 'date-fns/isToday';
import isTomorrow from 'date-fns/isTomorrow';


export const getTemplateEnum = (
  t: (key: string, value: string) => string,
): {
  [key: string]: string;
} => {
  return {
    single_account: `1:1 ${t('Individual', 'Individual')}`,
    round_robin_availability: `${t('Round_Robin', 'Round Robin')}`,
    round_robin_fairness: `${t('Round_Robin', 'Round Robin')}`,
    one_slot_multiple_event: `${t('MultiEvent', 'Multi-Event')}`,
    panel_interview: t('Panel', 'Panel'),
    one_event_multiple_attendee: `${t('MultiAttendee', 'Multi-Attendee')}`,
  };
};

export const getDurationLabel = (duration: Duration, t: TFunction): string => {
  const isPlural = duration.amount !== 1;

  const units = {
    minutes: isPlural ? t('Minutes', 'Minutes') : t('Minute', 'Minute'),
    hours: isPlural ? t('Hours', 'Hours') : t('Hour', 'Hour'),
    days: isPlural ? t('Days', 'Days') : t('Day', 'Day'),
    weeks: isPlural ? t('Weeks', 'Weeks') : t('Week', 'Week'),
  } as const;

  return `${duration.amount} ${units[duration.unit]}`;
};

export function formatDate(
  value: string,
  t: (key: string, value: string) => string,
  makeKey: (key: string) => string,
): string {
  const date = new Date(value);
  const day = format(date, 'EEEE');
  const month = format(date, 'MMMM');
  const dateNumber = date.getDate();
  const year = date.getFullYear();

  const dateString = `${t(makeKey(month), month)} ${dateNumber}, ${year}`;

  const commonDateString = `${t(makeKey(day), day)}, ${t(
    makeKey(month),
    month,
  )} ${dateNumber}, ${year}`;

  if (isToday(date)) {
    return `${t('Today', 'Today')} • ${dateString}`;
  } else if (isTomorrow(date)) {
    return `${t('Tomorrow', 'Tomorrow')}  • ${dateString}`;
  } else {
    return commonDateString;
  }
}

export const getMeetingLinkOptions = (
  conferenceIntegrations: ConferenceIntegrations,
  t: (key: string, value: string) => string,
): MenuOption[] => {
  const options: MenuOption[] = [
    {
      label: `${t('Custom_Location', 'Custom Location')}`,
      key: 'custom_location',
    },
  ];

  if (conferenceIntegrations.microsoft_teams) {
    options.push({
      label: `${t('Microsoft_Teams', 'Microsoft Teams')}`,
      key: 'microsoft_teams',
    });
  }

  if (conferenceIntegrations.google_meet) {
    options.push({
      label: `${t('Google_Meet', 'Google Meet')}`,
      key: 'google_meet',
    });
  }

  if (conferenceIntegrations.zoom) {
    options.push({
      label: `${t('Zoom', 'Zoom')}`,
      key: 'zoom',
    });
  }

  if (conferenceIntegrations.webex) {
    options.push({
      label: `${t('Webex', 'Webex')}`,
      key: 'webex',
    });
  }

  return options;
};
